import { useWatch } from '@astro/forms'
import { TextInput } from '../fields'

const STRENGTH = [
  { color: 'bg-red-400', label: 'Weak password', labelColor: 'text-red-500' },
  { color: 'bg-amber-400', label: 'Fair — add more variety', labelColor: 'text-amber-600' },
  { color: 'bg-yellow-400', label: 'Good — almost there', labelColor: 'text-yellow-600' },
  { color: 'bg-green-500', label: 'Strong password ✓', labelColor: 'text-green-600' },
]

function passwordScore(value) {
  let score = 0
  if (value.length >= 8) score++
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score++
  if (/\d/.test(value)) score++
  if (/[^A-Za-z0-9]/.test(value)) score++
  return score
}

function PasswordStrength() {
  const password = useWatch({ name: 'password' }) ?? ''
  const score = passwordScore(password)
  const tier = STRENGTH[Math.max(0, score - 1)]
  return (
    <>
      <div className="flex items-center gap-1.5 mt-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < score ? tier.color : 'bg-stone-200'}`} />
        ))}
      </div>
      {password.length === 0 ? (
        <p className="text-[11px] text-stone-400 mt-1.5 font-medium">Use 8+ characters with letters, numbers &amp; symbols.</p>
      ) : (
        <p className={`text-[11px] mt-1.5 font-semibold ${tier.labelColor}`}>{tier.label}</p>
      )}
    </>
  )
}

/**
 * `showPassword=false` renders the profile-edit variant (no password change here).
 * `lockAccountFields` renders email and mobile read-only, for the dashboard's
 * "Become an Astrologer" screen: the applicant already has an account, so those
 * two come from it and are not part of the application payload — editable
 * inputs there would promise a change the submit never makes.
 */
export default function StepBasicInfo({ showPassword = true, lockAccountFields = false }) {
  return (
    <div className="step-panel space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextInput
          name="first_name"
          label="First Name"
          placeholder="Ravi"
          rules={{ required: 'First name is required.' }}
        />
        <TextInput
          name="last_name"
          label="Last Name"
          placeholder="Sharma"
          rules={{ required: 'Last name is required.' }}
        />
      </div>
      <TextInput
        name="display_name"
        label="Display Name"
        placeholder="Name shown publicly to seekers, e.g. Astro Ravi Sharma"
        rules={{ required: 'Display name is required.' }}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextInput
          name="email"
          type="email"
          label="Email Address"
          placeholder="you@example.com"
          readOnly={lockAccountFields}
          hint={lockAccountFields ? '(from your account)' : undefined}
          rules={{
            required: 'Email is required.',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
          }}
        />
        <TextInput
          name="mobile_no"
          type="tel"
          label="Mobile Number"
          placeholder="+91 98765 43210"
          readOnly={lockAccountFields}
          hint={lockAccountFields ? '(from your account)' : undefined}
          rules={{
            required: 'Mobile number is required.',
            pattern: { value: /^[+\d][\d\s-]{9,14}$/, message: 'Enter a valid mobile number.' },
          }}
        />
      </div>
      {showPassword && (
        <div>
          <TextInput
            name="password"
            type="password"
            label="Password"
            placeholder="Minimum 8 complex characters"
            autoComplete="new-password"
            rules={{
              required: 'Password is required.',
              minLength: { value: 8, message: 'Password must be at least 8 characters.' },
            }}
          />
          <PasswordStrength />
        </div>
      )}
    </div>
  )
}
